import {useEffect, useRef, useState} from 'react';
import {ProjectionProps} from '.';
import streamSaver from 'streamsaver';
import decodeError from 'shared/util/decore-error';
import {errorNotification} from 'shared/services/notification';
import useApi, {baseURL} from 'shared/hooks/use-api';
import {SelectDataProps} from 'shared/types/select-data';
import {KeyValueProps} from 'shared/types/key-value';
import {UseInfiniteQueryResult} from 'react-query';
import {successNotification} from 'shared/services/notification';
import useToken from 'modules/auth/atoms/use-token';
import useAccessControls from 'modules/access-controls/atoms/use-access-controls';
import {FilterQueryProps, FilterValueProps} from 'shared/util/encode-filters';
import {translatedProps} from 'shared/util/translated-props';

export interface PaginationDataProps {
  data: any[];
  isLastPage: boolean;
  count: number;
  currentPage: number;
  [key: string]: any;
}

interface Props {
  url: string;
  projection: ProjectionProps;
  fetchFilters?: string[];
  inifityQuery: UseInfiniteQueryResult<PaginationDataProps>;
  extraParams?: any;
  reorderIndexes?: boolean | string[];
  translatedPropsKey: keyof typeof translatedProps;
}

export interface OrderByProps {
  field: string;
  order: number;
}

const useTableState = ({
  projection,
  url,
  fetchFilters,
  inifityQuery,
  extraParams,
  reorderIndexes,
  translatedPropsKey,
}: Props) => {
  const [loadingExportData, setLoadingExportData] = useState(false);
  const [filters, setFilters] = useState<KeyValueProps<SelectDataProps[]>>();
  const api = useApi();
  const {token} = useToken();
  const [loadingImportData, setLoadingImportData] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<any[]>([]);
  const {cityId, companyId} = useAccessControls();

  const handleChangeOrder = (field: string) => {
    if (!projection.orderBy) return;

    const {onChange, ...orderBy} = projection.orderBy;

    if (field === orderBy.field)
      return onChange({field, order: orderBy.order * -1});

    onChange({field, order: 1});
  };

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
    hasNextPage,
  } = inifityQuery;

  const count = data?.pages[0]?.count ?? 0;

  useEffect(() => {
    handleGetFilters();
  }, []);

  const handleExportData = async () => {
    if (count > 20 * 1000)
      return errorNotification({
        title: 'Erro ao exportar dados',
        message: 'Número de registros deve ser inferior a 20 mil',
      });

    setLoadingExportData(true);

    const {options, values} = projection;
    const filters = Object.keys(projection.filter?.filters ?? {}).reduce(
      (filters, key) => {
        const filter = projection.filter!.filters[key];
        const option = options.find((option) =>
          typeof option === 'string' ? key === option : option.value === key,
        );

        const filterCondition = Array.isArray(
          eval(filter as unknown as string),
        );

        if (!Array.isArray(filter) || typeof option === 'string')
          return {
            ...filters,
            [key]: filterCondition ? filter : JSON.stringify(filter),
          };

        const filterKey = option?.filterKey ?? (option?.value as string);

        return {
          ...filters,
          [filterKey]: filterCondition ? JSON.stringify(filter) : filter,
        };
      },
      {},
    );

    const extraFilters = Object.keys(extraParams ?? {}).reduce(
      (filters, key) => ({
        ...filters,
        [key]: JSON.stringify(extraParams[key]),
      }),
      {},
    );
    const response = await fetch(
      `${baseURL}${url}/xlsx?${new URLSearchParams({
        projection: values!.join(' '),
        translatedKeys: JSON.stringify(translatedProps[translatedPropsKey]),
        ...filters,
        ...extraFilters,
      }).toString()}`,
      {
        headers: {
          language: 'pt_BR',
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const {body, status} = response;

    if (status >= 400) {
      const data = await response.json();
      errorNotification({
        title: 'Erro ao exportar dados',
        message: data.message,
      });
    } else {
      const fileStream = streamSaver.createWriteStream('planilha.xlsx');
      body?.pipeTo(fileStream);
    }

    setLoadingExportData(false);
  };

  const handleGetFilters = async () => {
    if (!fetchFilters) return;

    try {
      const {data} = await api({
        url: `${url}/filters`,
        method: 'GET',
        params: {projection: fetchFilters.join(' ')},
      });
      setFilters(data.data);
    } catch (error) {
      errorNotification({
        title: 'Erro ao buscar filtros',
        message: decodeError(error),
      });
      setFilters(undefined);
    }
  };

  const reorderProjection = (from: string, to: string) => {
    const projectionValues = [...projection.values!];

    const fromIndex = projection.values?.indexOf(from);
    const toIndex = projection.values?.indexOf(to);

    if (
      typeof fromIndex !== 'number' ||
      typeof toIndex !== 'number' ||
      !projectionValues ||
      !projection.onChange
    )
      return;

    projectionValues.splice(
      toIndex,
      0,
      projectionValues.splice(fromIndex, 1)[0],
    );

    projection.onChange(projectionValues);
  };

  useEffect(() => {
    if (data) {
      setRows(
        data.pages.reduce((data, item) => [...data, ...item.data], [] as any[]),
      );
    }
  }, [data?.pages]);

  const reorderRows = async (fromId: string, toId: string) => {
    const reorderRows = [...rows];
    const fromIndex = reorderRows.findIndex((row) => row.id === fromId);
    const toIndex = reorderRows.findIndex((row) => row.id === toId);

    if (
      typeof fromIndex !== 'number' ||
      typeof toIndex !== 'number' ||
      reorderRows.length === 0
    )
      return;

    reorderRows.splice(toIndex, 0, reorderRows.splice(fromIndex, 1)[0]);

    setRows(reorderRows);

    const body = {} as any;

    const filters = projection.filter?.filters ?? ({} as FilterQueryProps);

    if (filters['category.name'])
      body['categoryId'] = (filters['category.name'] as FilterValueProps).value;
    if (filters['company.name'])
      body['companyId'] = (filters['company.name'] as FilterValueProps).value;
    if (cityId) body['cityId'] = cityId;
    if (companyId) body['companyId'] = companyId;

    if (Array.isArray(reorderIndexes))
      reorderIndexes.forEach((filterKey) => {
        const {value} = projection.filter!.filters[
          filterKey
        ] as FilterValueProps;

        body[filterKey] = value;
      });

    await api({
      url: `${url}/reorder-indexes`,
      method: 'PATCH',
      data: {fromId, toId, ...body},
    });

    refetch();
  };

  const handleGetTemplate = async () => {
    setLoadingTemplate(true);
    const {data} = await api({
      url: `${url}/xlsx/template`,
      method: 'GET',
    });
    window.open(data.data);
    setLoadingTemplate(false);
  };

  const handleImportData = async (file: File) => {
    setLoadingImportData(true);
    const formData = new FormData();

    formData.append('file', file);

    try {
      await api({
        url: `${url}/xlsx`,
        method: 'POST',
        data: formData,
      });

      successNotification({
        title: 'Importar dados',
        message: 'Planilha importada com sucesso!',
      });
    } catch (error) {
      errorNotification({
        title: 'Erro ao importar dados',
        message: decodeError(error),
      });
    }

    setLoadingImportData(false);
  };

  return {
    loadingExportData,
    handleExportData,
    filters,
    isLoading,
    data: rows,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    count,
    handleChangeOrder,
    reorderProjection,
    handleGetTemplate,
    handleImportData,
    loadingImportData,
    loadingTemplate,
    importInputRef,
    reorderRows,
  };
};

export default useTableState;
